import { useCitations, type Citation } from '../../lib/citations'

/**
 * The References row of an evidence panel: one line per ID the payload sent.
 *
 * `evidence[slot].refs` is a list of registry IDs, and on its own an ID is not
 * a source anybody can find. Each one is resolved through the registry the
 * backend serves (`lib/citations.ts`) and rendered as the work it names —
 * authors, year, published title, venue, and the entry's `url` as a link whose
 * href and text are both that `url`, exactly as served.
 *
 * **Every word is the endpoint's.** The ` · ` between the fields is
 * presentation, like the roll-up line's (R15); nothing else here is text, and
 * `References.test.tsx` holds every line to exactly those five fields joined by
 * it.
 *
 * **An ID is never dropped and never blank.** One the registry does not carry,
 * and every one while the registry is loading or after it failed to load, is
 * rendered exactly as the payload sent it, in a `.tag` — which is what this row
 * showed for all of them before the registry existed. The payload's order is
 * kept, duplicates included: the list is the payload's, not ours to tidy.
 *
 * The link opens in a new tab, so the Daily Status stays where it was, and with
 * `noreferrer` as well as `noopener`: a publisher has no business learning
 * which recording's page the reader came from.
 */
export function References({ refs }: { refs: readonly string[] }) {
  const registry = useCitations()
  return (
    <ul className="score-card__refs">
      {refs.map((ref, index) => {
        const citation = registry?.get(ref) ?? null
        return (
          <li key={`${index}:${ref}`} className="score-card__ref" data-ref={ref}>
            {citation === null ? <code className="tag">{ref}</code> : <CitationLine citation={citation} />}
          </li>
        )
      })}
    </ul>
  )
}

function CitationLine({ citation }: { citation: Citation }) {
  return (
    <>
      <span className="score-card__ref-authors">{citation.authors}</span>
      {' · '}
      <span className="score-card__ref-year">{citation.year}</span>
      {' · '}
      <cite className="score-card__ref-title">{citation.publishedTitle}</cite>
      {' · '}
      <span className="score-card__ref-venue">{citation.venue}</span>
      {' · '}
      <a className="score-card__ref-link" href={citation.url} target="_blank" rel="noopener noreferrer">
        {citation.url}
      </a>
    </>
  )
}
